import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { PortalHeader } from "@/components/portal/portal-header";
import { PortalSidebar } from "@/components/portal/portal-sidebar";
import { requireView } from "@/core/lib/guards";
import { SessionProvider } from "@/core/lib/session-context";
import { portalMeQuery } from "@/lib/portal-data";
import { ImpersonationBanner } from "@/ui/components/auth/impersonation-banner";
import { SidebarInset, SidebarProvider } from "@/ui/components/ui/sidebar";

/**
 * Employee self-service portal shell — the port of the `portal` server layout.
 *
 * Gated to accounts whose active view is "employee", which `resolveActiveView`
 * only allows when the account is linked to an employee record (a pure portal
 * login, or an admin/HR user who also has a profile and has switched in).
 * Renders a minimal header + nav, not the HR sidebar.
 */
export const Route = createFileRoute("/portal")({
  beforeLoad: async ({ context, location }) => {
    const auth = await requireView(context.queryClient, "employee", "/sign-in", location.href);
    return { auth };
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(portalMeQuery),
  component: PortalLayout,
});

function PortalLayout() {
  const { auth } = Route.useRouteContext();
  const { data: me } = useQuery(portalMeQuery);

  return (
    <SessionProvider initial={auth.session}>
      <SidebarProvider
        defaultOpen={
          typeof document === "undefined" ||
          !document.cookie.split("; ").some((c) => c === "sidebar_state=false")
        }
      >
        <PortalSidebar
          companyName={me?.companyName || "Employee portal"}
          companyLogo={me?.companyLogo ?? null}
          plan={me?.plan ?? "free"}
          counts={me?.counts}
        />
        <SidebarInset>
          <PortalHeader userName={auth.session.user.name} userEmail={auth.session.user.email} />
          <ImpersonationBanner session={auth.session} />
          <div className="w-full px-4 py-6 sm:px-6 sm:py-8">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </SessionProvider>
  );
}
